import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DateRange } from 'react-day-picker';
import { TimeDuration } from '@ais-ui/ui-core';
import { useTracker } from '@ais-ui/performance-tracking';

interface ReplayContextProps {
  children: ReactNode;
}

type ResetReplayParams = { resetDate?: boolean };
type ResetReplayType = (params: ResetReplayParams) => void;

interface ReplayContextValue {
  replaySpeed: number;
  setReplaySpeed: Dispatch<SetStateAction<number>>;
  secondsSinceStart: number;
  setSecondsSinceStart: Dispatch<SetStateAction<number>>;
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  replayOverlay: string | null;
  setReplayOverlay: Dispatch<SetStateAction<string | null>>;
  replayIdentifier: string | null;
  setReplayIdentifier: Dispatch<SetStateAction<string | null>>;
  isWaiting: boolean;
  resetReplay: ResetReplayType;
  maxTimeInSeconds: number;
  hasLoaded: boolean;
  setHasLoaded: Dispatch<SetStateAction<boolean>>;
  replayRequestDatetime: Date | null;
  setReplayRequestDatetime: Dispatch<SetStateAction<Date | null>>;
  startDate?: Date;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  duration: TimeDuration;
  setDuration: Dispatch<SetStateAction<TimeDuration>>;
  dateRange?: DateRange;
  minZoom: number;
  currentTime: Date | undefined;
  tracker: ReturnType<typeof useTracker>;
}

const ReplayContext = createContext<ReplayContextValue | undefined>(undefined);

export const ReplayProvider = ({ children }: ReplayContextProps) => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const minZoom = 9;

  const tracker = useTracker({ timeoutMs: 180000 });

  useEffect(() => {
    return () => {
      // Cancel any active transaction when provider actually unmounts
      if (tracker.isTracking) {
        tracker.cancelTransaction('ReplayProvider unmounting');
      }
    };
  }, []);

  const [duration, setDuration] = useState<TimeDuration>({
    hours: 0,
    minutes: 30,
  });

  const dateRange = useMemo(() => {
    if (!startDate) return undefined;
    if (!duration) return undefined;

    const endDate = new Date(startDate);
    const durationInMinutes = duration?.hours * 60 + duration?.minutes;
    endDate.setMinutes(startDate.getMinutes() + durationInMinutes);
    return { from: startDate, to: endDate };
  }, [startDate, duration]);

  const [replayOverlay, setReplayOverlay] = useState<string | null>(null);
  const [replayIdentifier, setReplayIdentifier] = useState<string | null>(null);
  const [replaySpeed, setReplaySpeed] = React.useState<number>(1);
  const [secondsSinceStart, setSecondsSinceStart] = React.useState<number>(0);
  const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
  const [isWaiting, setIsWaiting] = React.useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = React.useState<boolean>(false);
  const [replayRequestDatetime, setReplayRequestDatetime] =
    React.useState<Date | null>(null);

  const maxTimeInSeconds = useMemo(() => {
    if (!dateRange || !dateRange.to || !dateRange.from) return 0;

    const milliseconds = dateRange.to.getTime() - dateRange.from.getTime();
    return Math.floor(milliseconds / 1000); // Convert milliseconds to seconds
  }, [dateRange]);

  const resetReplay: ResetReplayType = useCallback(
    ({ resetDate }) => {
      setReplayOverlay(null);
      setReplayIdentifier(null);
      setReplaySpeed(1);
      setSecondsSinceStart(0);
      setIsPlaying(false);
      setIsWaiting(false);
      setHasLoaded(false);
      if (tracker.isTracking) {
        tracker.cancelTransaction('Replay reset');
      }
      if (resetDate) {
        setStartDate(undefined);
      }
    },
    [tracker],
  );

  const currentTime = useMemo(() => {
    if (!dateRange?.from) return undefined;
    return getDateInUtcWithAddedSeconds(dateRange.from, secondsSinceStart);
  }, [dateRange?.from, secondsSinceStart]);

  useEffect(() => {
    // Replay is currently playing
    if (isPlaying && replayIdentifier) {
      setIsWaiting(true);
    } else {
      if (isWaiting && !hasLoaded) {
        setHasLoaded(true);
      }
      setIsWaiting(false);
    }
  }, [hasLoaded, isPlaying, isWaiting, replayIdentifier]);

  return (
    <ReplayContext.Provider
      value={{
        replaySpeed,
        setReplaySpeed,
        secondsSinceStart,
        setSecondsSinceStart,
        isPlaying,
        setIsPlaying,
        replayOverlay,
        setReplayOverlay,
        replayIdentifier,
        setReplayIdentifier,
        isWaiting,
        resetReplay,
        maxTimeInSeconds,
        hasLoaded,
        setHasLoaded,
        replayRequestDatetime,
        setReplayRequestDatetime,
        startDate,
        setStartDate,
        duration,
        setDuration,
        dateRange,
        minZoom,
        currentTime,
        tracker,
      }}
    >
      {children}
    </ReplayContext.Provider>
  );
};

export const useReplayContext = () => {
  const context = useContext(ReplayContext);
  if (!context) {
    throw new Error('useReplayContext must be used within a ReplayProvider');
  }
  return context;
};

export const getDateInUtcWithAddedSeconds = (
  date: Date | undefined,
  secondsToAdd: number,
): Date | undefined => {
  if (!date) return;

  const fromTime = date.getTime();
  const addedTime = secondsToAdd * 1000; // Convert seconds to milliseconds
  const newDate = new Date(fromTime + addedTime);

  newDate.setMilliseconds(0);

  return newDate;
};
